import { FileText } from "lucide-react";
import { FILE_TYPE_COLORS, LANGUAGE_MAP, FILE_ICON_SIZE } from "../constants";

/**
 * Get the appropriate icon component for a file based on its extension
 * @param {string} fileName - The name of the file
 * @returns {JSX.Element} The icon component
 */
export const getFileIcon = (fileName) => {
  const ext = fileName.split(".").pop().toLowerCase();
  const colorClass = FILE_TYPE_COLORS[ext] || FILE_TYPE_COLORS.default;

  // Special cases for specific file types can be added here
  // For now, we use FileText with different colors
  return <FileText size={FILE_ICON_SIZE} className={colorClass} />;
};

/**
 * Get the Monaco editor language identifier for a file
 * @param {string} fileName - The name of the file
 * @returns {string} The language identifier for Monaco editor
 */
export const getLanguageFromFileName = (fileName) => {
  const ext = fileName.split(".").pop().toLowerCase();
  return LANGUAGE_MAP[ext] || "plaintext";
};

/**
 * Check if a file is a text file based on its extension
 * @param {string} fileName - The name of the file
 * @returns {boolean} True if the file is a text file
 */
export const isTextFile = (fileName) => {
  const textExtensions = [
    "txt",
    "md",
    "json",
    "js",
    "jsx",
    "ts",
    "tsx",
    "css",
    "scss",
    "sass",
    "less",
    "html",
    "htm",
    "xml",
    "py",
    "java",
    "cpp",
    "c",
    "h",
    "hpp",
    "cs",
    "php",
    "rb",
    "go",
    "rs",
    "kt",
    "swift",
    "scala",
    "sh",
    "bash",
    "yaml",
    "yml",
    "toml",
    "ini",
    "cfg",
    "conf",
    "env",
    "sql",
    "dockerfile",
    "makefile",
    "gitignore",
  ];
  const ext = fileName.split(".").pop().toLowerCase();
  return textExtensions.includes(ext);
};

/**
 * Format file size in human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

/**
 * Get file extension
 * @param {string} fileName - The name of the file
 * @returns {string} The file extension
 */
export const getFileExtension = (fileName) => {
  const parts = fileName.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

/**
 * Check if a file is an image
 * @param {string} fileName - The name of the file
 * @returns {boolean} True if the file is an image
 */
export const isImageFile = (fileName) => {
  const imageExtensions = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "svg",
    "webp",
    "bmp",
    "ico",
  ];
  const ext = getFileExtension(fileName);
  return imageExtensions.includes(ext);
};

/**
 * Check if a file is a binary file (non-text)
 * @param {string} fileName - The name of the file
 * @returns {boolean} True if the file is likely binary
 */
export const isBinaryFile = (fileName) => {
  const binaryExtensions = [
    "exe",
    "dll",
    "so",
    "dylib",
    "bin",
    "pdf",
    "doc",
    "docx",
    "xls",
    "xlsx",
    "ppt",
    "pptx",
    "zip",
    "tar",
    "gz",
    "rar",
    "7z",
    "mp3",
    "mp4",
    "avi",
    "mov",
    "wmv",
    "flv",
    "ttf",
    "otf",
    "woff",
    "woff2",
    "eot",
  ];
  const ext = getFileExtension(fileName);
  return binaryExtensions.includes(ext) || isImageFile(fileName);
};
